import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface JsonViewProps {
  value: unknown;
  className?: string;
  maxBytes?: number;
}

export function JsonView({ value, className, maxBytes = 100_000 }: JsonViewProps): JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const { display, truncated, raw } = React.useMemo(() => {
    const text = safeStringify(value);
    if (text.length <= maxBytes) return { display: text, truncated: false, raw: text };
    return {
      display: `${text.slice(0, maxBytes)}\n… [tronqué — ${text.length - maxBytes} octets supplémentaires]`,
      truncated: true,
      raw: text,
    };
  }, [value, maxBytes]);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success(truncated ? 'JSON complet copié (la prévisualisation est tronquée)' : 'JSON copié');
    } catch {
      toast.error('Échec de la copie');
    }
  }

  return (
    <div className={cn('relative', className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={copy}
        className="absolute right-2 top-2 h-7 gap-1.5 bg-background/80 px-2 text-xs backdrop-blur"
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        {copied ? 'Copié' : 'Copier'}
      </Button>
      <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-3 pr-20 text-[11px] leading-relaxed">
        {display}
      </pre>
    </div>
  );
}

function safeStringify(value: unknown): string {
  if (value === null || value === undefined) return String(value);
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
